import type { ParentComponent } from "solid-js";
import type { FormValue } from "~/types/common";

import { createContext, onMount, useContext } from "solid-js";
import { createStore } from "solid-js/store";
import { useModalFormContext } from "./ModalFormContext";
import { useSearchParams } from "@solidjs/router";
import type {
  FormSystemName,
  FormSystemNameWithSubject,
} from "~/types/form_system_name";
import { getWrapper } from "~/lib/fetcher";
import { useEventsContext } from "./EventsContext";

export type FormStorage = [
  { values: Record<string, FormValue> },
  {
    setValue?: (name: string, value: FormValue) => void;
    unsetValue?: (name: string) => void;
  },
];

const FormContext = createContext<FormStorage>([{ values: {} }, {}]);

export const FormContextProvider: ParentComponent<{
  values?: Record<string, FormValue>;
}> = (props) => {
  const [state, setState] = createStore({
      // eslint-disable-next-line solid/reactivity
      values: props.values || {},
    }),
    store: FormStorage = [
      state,
      {
        setValue(name: string, value: FormValue) {
          setState("values", (prev) => ({ ...prev, [name]: value }));
        },

        unsetValue(name: string) {
          setState("values", name, undefined);
        },
      },
    ];

  return (
    <FormContext.Provider value={store}>{props.children}</FormContext.Provider>
  );
};

export function useFormContext() {
  return useContext(FormContext);
}

export async function OnLoadForm() {
  const [searchParams] = useSearchParams();
  const [, { sendShowEvent }] = useEventsContext();
  const [, { openModalForm, setModalFormHeadline }] = useModalFormContext();

  const handledForms = [
    "callback",
    "advisor",
    "leaflet",
    "blueprint",
    "sponsorship",
    "qualification",
    "financial-study",
    "guide-invest",
  ];
  onMount(async () => {
    if (
      searchParams.form &&
      handledForms.includes(searchParams.form as string)
    ) {
      let wrapper, lot, subject;
      if (searchParams.program_nid) {
        wrapper = await getWrapper(
          parseInt(searchParams.program_nid as string),
        );
      }

      if (searchParams.lot_id) {
        lot = wrapper?.lots.find(
          (lot) =>
            lot.drupal_internal__id === parseInt(searchParams.lot_id as string),
        );
      }

      if (searchParams.subject && searchParams.form === "advisor") {
        switch (searchParams.subject) {
          case "1":
            subject = "question";
            sendShowEvent("advisor-question", "modal");
            break;
          case "2":
            subject = "rdv";
            sendShowEvent("advisor-appointment", "modal");
            break;
          case "3":
            subject = "appel";
            sendShowEvent("advisor-callback", "modal");
            break;
        }
      } else {
        sendShowEvent(searchParams.form as FormSystemNameWithSubject, "modal");
      }

      openModalForm!(searchParams.form as FormSystemName, {
        wrapper,
        lot,
        defaultValues: {
          subject,
        },
      });

      switch (searchParams.form as FormSystemName) {
        case "callback":
          setModalFormHeadline!("Être appelé");
          break;
        case "advisor":
          setModalFormHeadline!("Contacter un conseiller");
          break;
        case "leaflet":
          setModalFormHeadline!("Télécharger la brochure");
          break;
        case "blueprint":
          setModalFormHeadline!("Télécharger le plan");
          break;
        case "sponsorship":
          setModalFormHeadline!("Parrainer vos proches");
          break;
        case "qualification":
          setModalFormHeadline!("Préciser votre besoin");
          break;
        case "financial-study":
          setModalFormHeadline!("Étude personnalisée");
          break;
        case "guide-invest":
          setModalFormHeadline!("Le guide de l’investissement immobilier");
          break;
      }
    }
  });

  return <></>;
}
